/* eslint-disable react/require-default-props */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
/**
 * Determines the file type category based on MIME type
 */
function getFileType(file) {
    if (file.type.startsWith('image/'))
        return 'image';
    if (file.type.startsWith('audio/'))
        return 'audio';
    return 'document';
}
/**
 * Generates a unique ID for attachments
 */
function generateId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
function toAttachment(file) {
    return { id: generateId(), file, name: file.name, type: getFileType(file) };
}
const AIInputContext = createContext(null);
/**
 * AIInput Context Provider
 * Manages all state for the AIInput organism
 * @version 1.0.0
 * @since 2026-02-17
 */
export function AIInputProvider({ allowedFormats, maxFiles = 10, onMaxFilesExceeded, defaultValue, defaultFiles, children }) {
    const [text, setText] = useState(defaultValue || '');
    const [attachments, setAttachments] = useState(() => (defaultFiles !== null && defaultFiles !== void 0 ? defaultFiles : []).map(toAttachment));
    // Read at call time: the room left must be judged against what is attached NOW, outside the
    // state updater (which must stay free of side effects).
    const attachmentCountRef = useRef(0);
    attachmentCountRef.current = attachments.length;
    const [isAttachMenuOpen, setAttachMenuOpen] = useState(false);
    // Sync text when defaultValue changes (e.g., retry functionality)
    useEffect(() => {
        if (defaultValue !== undefined) {
            setText(defaultValue);
        }
    }, [defaultValue]);
    const addAttachments = useCallback((files) => {
        const filtered = allowedFormats
            ? files.filter((file) => {
                const extension = file.name.split('.').pop();
                const ext = extension ? `.${extension.toLowerCase()}` : '';
                return allowedFormats.some((fmt) => fmt.toLowerCase() === ext);
            })
            : files;
        if (filtered.length > Math.max(maxFiles - attachmentCountRef.current, 0))
            onMaxFilesExceeded === null || onMaxFilesExceeded === void 0 ? void 0 : onMaxFilesExceeded(maxFiles);
        setAttachments((prev) => {
            const remaining = maxFiles - prev.length;
            if (remaining <= 0)
                return prev;
            return [...prev, ...filtered.slice(0, remaining).map(toAttachment)];
        });
    }, [allowedFormats, maxFiles, onMaxFilesExceeded]);
    const removeAttachment = useCallback((id) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
    }, []);
    const clearAll = useCallback(() => {
        setText('');
        setAttachments([]);
    }, []);
    const value = useMemo(() => ({
        text,
        setText,
        attachments,
        addAttachments,
        removeAttachment,
        clearAll,
        isAttachMenuOpen,
        setAttachMenuOpen
    }), [text, attachments, addAttachments, removeAttachment, clearAll, isAttachMenuOpen]);
    return React.createElement(AIInputContext.Provider, { value: value }, children);
}
/**
 * Hook to consume AIInput context
 * Must be used within an AIInputProvider
 */
export function useAIInput() {
    const context = useContext(AIInputContext);
    if (!context) {
        throw new Error('useAIInput must be used within an AIInputProvider');
    }
    return context;
}
